export type TimelineMachine = {
  mac_id: string;
  name: string;
  is_door_open?: boolean | null;
  remaining_seconds?: number | null;
  remaining_vend?: number | null;
  can_top_off?: boolean | null;
  top_off_vend?: number | null;
  top_off_time?: number | null;
  selected_cycle_id?: string | null;
  selected_cycle_name?: string | null;
  selected_temperature_id?: string | null;
  selected_temperature_name?: string | null;
  selected_modifiers?: unknown;
  alliance_received_at?: string | Date | null;
};

export type TimelineSegment = {
  mac_id: string | null;
  Machine: string;
  statusName: string;
  siteId?: string;
  start_time: string | Date;
  end_time: string | Date | null;
  /** Cycle that was selected when this period ended. Null while the period is still open. */
  selected_cycle_id?: string | null;
  selected_cycle_name?: string | null;
  selected_temperature_name?: string | null;
  /** Coded Alliance error from machineErrorLogs (joined for ERROR segments). */
  errorcode?: string | number | null;
  errorname?: string | null;
  errortype?: string | null;
};

export type TimelineGatewayStatus = 'online' | 'offline' | 'unknown';

export type TimelineGateway = {
  gatewayName: string;
  gatewayStatus: TimelineGatewayStatus;
  latestStatusUpdate: string | null;
  checkedAt: string;
  macAddress: string | null;
  antennaType: string | null;
  releaseVersion: number | null;
};

export type TimelineStatusCategory =
  | 'available'
  | 'ready'
  | 'complete'
  | 'in-use'
  | 'error'
  | 'out-of-order'
  | 'unknown';

export type MachineTypeFilter = 'all' | 'washers' | 'dryers';

export type StatusCategoryFilter = 'all' | TimelineStatusCategory;

export const TIMELINE_PRESETS = [
  { label: '15m', ms: 15 * 60 * 1000 },
  { label: '1h', ms: 60 * 60 * 1000 },
  { label: '6h', ms: 6 * 60 * 60 * 1000 },
  { label: '24h', ms: 24 * 60 * 60 * 1000 },
] as const;

export const STATUS_FILTER_CHIPS: { id: StatusCategoryFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'available', label: 'Available' },
  { id: 'ready', label: 'Ready to start' },
  { id: 'in-use', label: 'In use' },
  { id: 'complete', label: 'Complete' },
  { id: 'error', label: 'Error' },
  { id: 'out-of-order', label: 'Out of order' },
];

export function getTimelineStatusCategory(status: string): TimelineStatusCategory {
  if (!status) return 'unknown';
  const upper = status.toUpperCase().replace(/_/g, ' ');

  if (
    upper.includes('OUT OF ORDER') ||
    upper.includes('OUTOFORDER') ||
    upper.includes('MAINTENANCE') ||
    upper.includes('FAULT') ||
    upper.includes('BROKEN') ||
    upper.includes('DOWN')
  ) {
    return 'out-of-order';
  }

  if (
    upper.includes('NETWORK ERROR') ||
    upper.includes('NETWORKERROR') ||
    upper.includes('NETWORK') ||
    upper.includes('UNAVAILABLE') ||
    (upper.includes('ERROR') && !upper.includes('OUT OF ORDER'))
  ) {
    return 'error';
  }

  if (
    upper.includes('IN USE') ||
    upper.includes('INUSE') ||
    status.includes('In Use')
  ) {
    return 'in-use';
  }

  if (upper.includes('COMPLETE') || upper.includes('COMPLETED')) {
    return 'complete';
  }

  // Credit loaded / waiting to start — distinct from COMPLETE (cycle finished).
  if (upper.includes('READY') && !upper.includes('IN USE')) {
    return 'ready';
  }

  if (
    upper.includes('AVAILABLE') ||
    upper.includes('IDLE') ||
    upper.includes('FREE')
  ) {
    return 'available';
  }

  return 'unknown';
}

export function formatTimelineStatus(status: string): string {
  if (!status) return 'Unknown';
  return status
    .replace(/_/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function toMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

export function formatAxisTime(ms: number, spanMs: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  if (spanMs > 6 * 60 * 60 * 1000) {
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return time;
}

/** Human-readable duration, e.g. 45s, 3m 12s, 1h 5m, 2d 4h. */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) ms = 0;
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;

  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

/** Prefer "Fill failure (11)" / "11 — Fill failure" style for ERROR bars. */
export function formatTimelineErrorDetail(
  seg: Pick<TimelineSegment, 'errorcode' | 'errorname'>
): string | null {
  const code =
    seg.errorcode != null && String(seg.errorcode).trim() !== ''
      ? String(seg.errorcode).trim()
      : '';
  const name =
    seg.errorname != null && String(seg.errorname).trim() !== ''
      ? String(seg.errorname).trim()
      : '';
  if (code && name && name !== code) return `${name} (${code})`;
  if (name) return name;
  if (code) return code;
  return null;
}

export function formatSegmentLabel(
  status: string,
  startMs: number,
  endMs: number,
  errorDetail?: string | null
): string {
  const duration = formatDuration(endMs - startMs);
  const statusText = formatTimelineStatus(status).toLowerCase();
  const detail = (errorDetail || '').trim();
  if (detail) return `${statusText} · ${detail} · ${duration}`;
  return `${statusText} · ${duration}`;
}

/** In-bar label with optional live cycle (e.g. open in-use segments) or coded error. */
export function formatSegmentLabelWithCycle(
  status: string,
  startMs: number,
  endMs: number,
  cycleLabel: string | null | undefined,
  errorDetail?: string | null
): string {
  const duration = formatDuration(endMs - startMs);
  const statusText = formatTimelineStatus(status).toLowerCase();
  const cycle = (cycleLabel || '').trim();
  const detail = (errorDetail || '').trim();
  if (cycle) return `${statusText} · ${cycle} · ${duration}`;
  if (detail) return `${statusText} · ${detail} · ${duration}`;
  return `${statusText} · ${duration}`;
}

/** Sort washers then dryers by numeric suffix (W1, W2, … D1, D2). */
export function compareMachineNames(a: string, b: string): number {
  const typeOrder = (name: string) => {
    const c = (name || '').charAt(0).toUpperCase();
    if (c === 'W') return 1;
    if (c === 'D') return 2;
    return 3;
  };
  const ta = typeOrder(a);
  const tb = typeOrder(b);
  if (ta !== tb) return ta - tb;
  const na = parseInt((a.match(/\d+/) || ['0'])[0], 10);
  const nb = parseInt((b.match(/\d+/) || ['0'])[0], 10);
  if (na !== nb) return na - nb;
  return (a || '').localeCompare(b || '');
}

/** Prefer display name so logs without mac_id merge with LiveMachineStatus rows. */
export function machineKey(macId: string | null | undefined, name: string): string {
  const n = (name || '').trim();
  if (n) return n;
  return (macId || '').trim();
}

/** Deduplicate machines by display name, keeping the richest row (prefer mac_id + detail). */
export function dedupeMachines(machines: TimelineMachine[]): TimelineMachine[] {
  const byName = new Map<string, TimelineMachine>();
  for (const m of machines) {
    const name = (m.name || '').trim();
    if (!name) continue;
    const existing = byName.get(name);
    if (!existing) {
      byName.set(name, { ...m, name, mac_id: m.mac_id || '' });
    } else if (!existing.mac_id && m.mac_id) {
      byName.set(name, { ...m, name, mac_id: m.mac_id });
    } else if (
      existing.alliance_received_at == null &&
      m.alliance_received_at != null
    ) {
      byName.set(name, { ...existing, ...m, name, mac_id: existing.mac_id || m.mac_id || '' });
    }
  }
  return Array.from(byName.values());
}

/** Format remaining seconds as m:ss (or h:mm:ss if long). */
export function formatRemainingClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  }
  return `${m}:${String(sec).padStart(2, '0')}`;
}

/**
 * Countdown remaining seconds between polls using alliance_received_at as baseline.
 * Falls back to raw remaining_seconds when received_at is missing.
 */
export function effectiveRemainingSeconds(
  row: TimelineMachine,
  nowMs: number
): number | null {
  const raw = row.remaining_seconds;
  if (raw == null || !Number.isFinite(Number(raw)) || Number(raw) <= 0) {
    return null;
  }
  const remaining = Number(raw);
  const received = row.alliance_received_at
    ? new Date(row.alliance_received_at).getTime()
    : NaN;
  if (Number.isNaN(received)) return remaining;
  const elapsed = Math.max(0, (nowMs - received) / 1000);
  return Math.max(0, remaining - elapsed);
}

export type CycleLabelFields = {
  selected_cycle_id?: string | null;
  selected_cycle_name?: string | null;
  selected_temperature_name?: string | null;
};

/** Friendly cycle label — never show raw Alliance ids like cyc_1. */
export function formatCycleLabel(row: CycleLabelFields): string | null {
  const name = (row.selected_cycle_name || '').trim();
  const id = (row.selected_cycle_id || '').trim();
  let cycle = name;
  if (!cycle && id) {
    const stripped = id.replace(/^cyc_/i, '').replace(/_/g, ' ').trim();
    if (/^\d+$/.test(stripped)) cycle = `Cycle ${stripped}`;
    else if (stripped) {
      cycle = stripped.replace(/\b\w/g, (ch) => ch.toUpperCase());
    }
  }
  if (!cycle) return null;
  const temp = (row.selected_temperature_name || '').trim();
  return temp ? `${cycle} · ${temp}` : cycle;
}

/**
 * Cycle text for an in-use bar. Open bars use the live machine snapshot.
 * Closed bars use the cycle stored on the segment when that period ended.
 */
export function segmentCycleLabel(
  seg: TimelineSegment,
  machine?: TimelineMachine | null
): string | null {
  if (getTimelineStatusCategory(seg.statusName) !== 'in-use') return null;
  if (seg.end_time == null && machine) return formatCycleLabel(machine);
  return formatCycleLabel(seg);
}

export function machineLiveTooltip(row: TimelineMachine): string {
  const parts = [row.mac_id || row.name];
  if (row.is_door_open === true) parts.push('Door open');
  const cycle = formatCycleLabel(row);
  if (cycle) parts.push(cycle);
  if (row.remaining_seconds != null && row.remaining_seconds > 0) {
    parts.push(`Remaining ${formatRemainingClock(row.remaining_seconds)}`);
  }
  return parts.filter(Boolean).join('\n');
}

/** Whether a row's most recent (open) segment is an out-of-order/maintenance status. */
export function isRowCurrentlyOutOfOrder(
  row: TimelineMachine,
  segments: TimelineSegment[]
): boolean {
  const key = machineKey(row.mac_id, row.name);
  const open = segments.find(
    (s) => machineKey(s.mac_id, s.Machine) === key && s.end_time == null
  );
  return !!open && getTimelineStatusCategory(open.statusName) === 'out-of-order';
}

/** The machine's current state: its open segment, else the most recent one. */
export function currentSegmentForRow(
  row: TimelineMachine,
  segments: TimelineSegment[]
): TimelineSegment | null {
  let latest: TimelineSegment | null = null;
  let latestStart = -Infinity;
  for (const seg of segments) {
    const segName = (seg.Machine || '').trim();
    const matches =
      (segName && segName === row.name) ||
      (!!seg.mac_id && !!row.mac_id && seg.mac_id === row.mac_id);
    if (!matches) continue;
    if (seg.end_time == null) return seg;
    const start = toMs(seg.start_time);
    if (start != null && start > latestStart) {
      latest = seg;
      latestStart = start;
    }
  }
  return latest;
}

export function matchesMachineType(name: string, filter: MachineTypeFilter): boolean {
  if (filter === 'all') return true;
  const c = (name || '').charAt(0).toUpperCase();
  if (filter === 'washers') return c === 'W';
  if (filter === 'dryers') return c === 'D';
  return true;
}

export function filterTimelineRows(
  rows: TimelineMachine[],
  segments: TimelineSegment[],
  opts: {
    search: string;
    statusFilter: StatusCategoryFilter;
    typeFilter: MachineTypeFilter;
    windowStartMs: number;
    windowEndMs: number;
  }
): TimelineMachine[] {
  const q = opts.search.trim().toLowerCase();
  const now = Date.now();

  return rows.filter((row) => {
    if (!matchesMachineType(row.name, opts.typeFilter)) return false;
    if (q && !row.name.toLowerCase().includes(q) && !(row.mac_id || '').toLowerCase().includes(q)) {
      return false;
    }
    if (opts.statusFilter === 'all') return true;

    const rowSegs = segments.filter((s) => {
      const segName = (s.Machine || '').trim();
      return (
        (segName && segName === row.name) ||
        (!!s.mac_id && !!row.mac_id && s.mac_id === row.mac_id)
      );
    });

    return rowSegs.some((s) => {
      const start = toMs(s.start_time);
      if (start == null) return false;
      const end = toMs(s.end_time) ?? Math.min(now, opts.windowEndMs);
      if (end <= opts.windowStartMs || start >= opts.windowEndMs) return false;
      return getTimelineStatusCategory(s.statusName) === opts.statusFilter;
    });
  });
}

export type TimelineSummary = {
  availablePct: number;
  inUsePct: number;
  errorOfflineMs: number;
  machinesInErrorNow: number;
  machineCount: number;
  windowMs: number;
};

export function computeTimelineSummary(
  machines: TimelineMachine[],
  segments: TimelineSegment[],
  windowStartMs: number,
  windowEndMs: number
): TimelineSummary {
  const machineCount = Math.max(machines.length, 1);
  const windowMs = Math.max(windowEndMs - windowStartMs, 1);
  const denom = machineCount * windowMs;
  const now = Date.now();

  // Restrict to segments belonging to the given machine set so filtered views
  // (search / type / status filters) report metrics for what's on screen, not
  // the whole site. Without this, e.g. filtering to "Dryers" still summed every
  // machine's segments while dividing by the (smaller) dryer count.
  const names = new Set(machines.map((m) => m.name));
  const macIds = new Set(machines.filter((m) => m.mac_id).map((m) => m.mac_id));
  const belongsToMachines = (seg: TimelineSegment) => {
    const segName = (seg.Machine || '').trim();
    if (segName && names.has(segName)) return true;
    return !!(seg.mac_id && macIds.has(seg.mac_id));
  };

  let availableMs = 0;
  let inUseMs = 0;
  let errorOfflineMs = 0;
  let machinesInErrorNow = 0;

  const openErrorMachines = new Set<string>();

  for (const seg of segments) {
    if (!belongsToMachines(seg)) continue;
    const start = toMs(seg.start_time);
    if (start == null) continue;
    const rawEnd = toMs(seg.end_time);
    const end = rawEnd ?? Math.min(now, windowEndMs);
    const clippedStart = Math.max(start, windowStartMs);
    const clippedEnd = Math.min(end, windowEndMs);
    if (clippedEnd <= clippedStart) continue;

    const dur = clippedEnd - clippedStart;
    const cat = getTimelineStatusCategory(seg.statusName);
    if (cat === 'available') availableMs += dur;
    else if (cat === 'in-use') inUseMs += dur;
    else if (cat === 'error' || cat === 'out-of-order') errorOfflineMs += dur;

    if (seg.end_time == null && cat === 'error') {
      openErrorMachines.add(machineKey(seg.mac_id, seg.Machine));
    }
  }

  machinesInErrorNow = openErrorMachines.size;

  return {
    availablePct: Math.round((availableMs / denom) * 1000) / 10,
    inUsePct: Math.round((inUseMs / denom) * 1000) / 10,
    errorOfflineMs,
    machinesInErrorNow,
    machineCount: machines.length,
    windowMs,
  };
}

export function segmentsToCsv(
  segments: TimelineSegment[],
  windowStartMs: number,
  windowEndMs: number,
  machineFilter?: Set<string>,
  machines?: TimelineMachine[]
): string {
  const now = Date.now();
  const machinesByKey = new Map<string, TimelineMachine>();
  for (const machine of machines || []) {
    machinesByKey.set(machineKey(machine.mac_id, machine.name), machine);
  }
  const header = 'machine,mac_id,status,cycle,start,end,duration_seconds';
  const lines = [header];

  for (const seg of segments) {
    const name = (seg.Machine || '').trim();
    const key = machineKey(seg.mac_id, name);
    if (machineFilter && !machineFilter.has(key) && !machineFilter.has(name)) continue;

    const start = toMs(seg.start_time);
    if (start == null) continue;
    const end = toMs(seg.end_time) ?? Math.min(now, windowEndMs);
    const clippedStart = Math.max(start, windowStartMs);
    const clippedEnd = Math.min(end, windowEndMs);
    if (clippedEnd <= clippedStart) continue;

    const durationSec = Math.round((clippedEnd - clippedStart) / 1000);
    const endIso = seg.end_time == null ? '' : new Date(clippedEnd).toISOString();
    const cycle = segmentCycleLabel(seg, machinesByKey.get(key)) || '';
    const esc = (v: string) => `"${String(v).replace(/"/g, '""')}"`;
    lines.push(
      [
        esc(name),
        esc(seg.mac_id || ''),
        esc(seg.statusName || ''),
        esc(cycle),
        esc(new Date(clippedStart).toISOString()),
        esc(endIso),
        String(durationSec),
      ].join(',')
    );
  }

  return lines.join('\n');
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Local datetime-local input value from ms */
export function toDatetimeLocalValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromDatetimeLocalValue(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}
